import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { ReactiveFormsModule } from '@angular/forms'
import { ContactFormReqDto, pickPropsAssertDefined } from '@pai/commons'

import { AnalyticsService } from 'app/core/analytics/analytics.service'
import { AnalyticsEvent } from 'app/core/analytics/analytics-event.enum'
import { SectionContact } from 'app/core/cms/generated/api-types'
import { FormsApi } from 'app/core/forms-api'
import { CURRENT_LANG } from 'app/core/i18n/language.token'
import { LoggerService } from 'app/core/logger/logger.service'
import { ButtonComponent } from 'app/shared/button/button.component'
import { ContactForm } from 'app/shared/contact-form/contact.form'
import { ContactFormComponent } from 'app/shared/contact-form/contact-form.component'
import { LexicalRichTextDirective } from 'app/shared/richtext/lexical-rich-text.directive'
import { TranslatePipe } from 'app/shared/translate.pipe'
import { action } from 'app/utils/action'
import { injectApprovalResource } from 'app/utils/inject-approval-resource.function'
import { filterIfTruthy } from 'app/utils/rxjs/filter-if-truthy.operator'

import { CmsBlock } from '../../cms-block'
import { parseDesign } from '../section-utils'

// todo refactor to exist as Layout + Component (like NewsletterSubscription is already Component)

@Component({
  selector: 'pai-section-contact',
  templateUrl: './section-contact.component.html',
  styleUrls: ['./section-contact.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.background]': 'design().background',
    '[attr.innerShadow]': 'design().innerShadow',
  },
  imports: [ReactiveFormsModule, TranslatePipe, ButtonComponent, ContactFormComponent, LexicalRichTextDirective],
  providers: [{ provide: CmsBlock, useExisting: SectionContactComponent }],
})
export class SectionContactComponent extends CmsBlock<SectionContact> {
  readonly __typename = 'SectionContact'
  protected readonly design = computed(() => parseDesign(this.data().design))

  readonly #currentLanguage = inject(CURRENT_LANG)
  readonly #analytics = inject(AnalyticsService)
  readonly #logger = inject(LoggerService).getInstance('SectionContactComponent')

  protected readonly form = new ContactForm()
  protected readonly submitAction = action(inject(FormsApi).sendContactForm)

  private readonly approval = injectApprovalResource()

  constructor() {
    super()
    toObservable(this.submitAction.result).pipe(filterIfTruthy()).subscribe(this.onFormResult)
    toObservable(this.submitAction.error).pipe(filterIfTruthy()).subscribe(this.onFormError)
  }

  protected onSubmit() {
    if (!this.form.valid || this.submitAction.isPending() || this.approval.isLoading()) {
      return
    }
    const payload: ContactFormReqDto = {
      language: this.#currentLanguage,
      ...pickPropsAssertDefined(this.form.value, 'name', 'email', 'message'),
      approval: this.approval.value(),
    }
    this.submitAction.exec(payload)
    this.#analytics.trackEvent(AnalyticsEvent.CONTACT_FORM_SUBMIT)
  }

  private readonly onFormResult = () => {
    this.form.reset()
  }
  private readonly onFormError = (error: unknown) => {
    this.#logger.error('Form Submit failed', error)
  }
}
